import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import Select, { components, DropdownIndicatorProps } from 'react-select'
import { QuestionnaireFormLabel } from 'spa/member/components/QuestionnaireFormLabel'
import { MaterialSymbol } from 'spa/member/components/MaterialSymbol'
import { FormErrorMessage } from 'spa/member/components/FormErrorMessage'

export type StyledNumberSelectFormProps = {
  name: string
  label: string
  annotationNote?: string
  required?: boolean
  rules?: any
  start: number
  end: number
  step: number
  inputSize?: 'default' | 'small'
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void,
  dataCy?: string
} & React.InputHTMLAttributes<HTMLInputElement>

const DropdownIndicator: React.FC<DropdownIndicatorProps> = props => {
  return (
    <components.DropdownIndicator {...props}>
      <MaterialSymbol name="keyboard_arrow_down" className="text-member-gray-100" style={{ fontSize: '1rem' }} />
    </components.DropdownIndicator>
  );
};

export const StyledNumberSelectForm: React.FC<StyledNumberSelectFormProps> = (props: StyledNumberSelectFormProps) => {
  const { control } = useFormContext();
  const generateOptions = (start: number, end: number, step: number) => {
    const options = [];
    for (let i = start; i <= end; i += step) {
      options.push({ value: i, label: i.toLocaleString() });
    }
    return options;
  }
  const options = generateOptions(props.start, props.end, props.step);
  const [filteredOptions, setFilteredOptions] = useState(options)
  const { formState: { errors } } = useFormContext();
  const hasError = !!errors[props.name]

  const handleInputChange = (inputValue: string, { action }: { action: string }) => {
    if (action === 'input-change' && inputValue) {
      const cleanInput = inputValue.replace(/,/g, '');
      const filtered = options.filter(option =>
        option.value.toString().startsWith(cleanInput)
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }

  const conditionalColorClass = hasError ? 'border-member-red-100' : 'border-member-gray-40'
  const sizeClass = props.inputSize === 'small' ? 'text-S px-S py-XS' : 'text-M p-S'
  const controlClass = (state: { isFocused: boolean }) => `
    border-1 ${conditionalColorClass} rounded ${sizeClass} leading-4 w-full text-member-gray-100
    disabled:bg-member-gray-30 ${state.isFocused ? 'shadow-none' : ''}
  `

  const menuClass = props.inputSize === 'small' ? 'text-S text-member-gray-100' : 'text-M text-member-gray-100'

  return (
    <div data-cy={props.dataCy}>
      <QuestionnaireFormLabel htmlFor={props.name} label={props.label} required={props.required} annotationNote={props.annotationNote}/>
      <Controller
        name={props.name}
        control={control}
        rules={props.rules}
        render={({ field }) => (
          <>
            <Select
              {...field}
              components={{ DropdownIndicator, IndicatorSeparator: () => null }}
              options={filteredOptions}
              value={options.find(option => option.value === Number(field.value))}
              onChange={(selectedOption) => {
                field.onChange(selectedOption.value);
                props.onChange?.({ target: { value: selectedOption.value } } as React.ChangeEvent<HTMLSelectElement>);
                }}
              onInputChange={handleInputChange}
              classNamePrefix="select"
              styles={
                {
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: state.isFocused ? 'var(--color-member-gray-10) !important' : baseStyles.backgroundColor,
                    borderColor: state.isFocused ? 'var(--color-member-green-100) !important' : baseStyles.borderColor,
                  }),
                  placeholder: (baseStyles) => ({
                    ...baseStyles,
                    color: 'var(--color-member-gray-100)'
                  }),
                }
              }
              classNames={{
                control: (state) => controlClass(state),
                input: () => 'p-0 m-0',
                singleValue: () => 'm-0',
                valueContainer: () => 'p-0',
                placeholder: () => 'm-0',
                dropdownIndicator: () => 'p-0',
                menu: () => menuClass
              }}
              placeholder="選択してください"
            />
            {hasError && <FormErrorMessage message={String(errors[props.name]?.message)} />}
          </>
        )}
      />
    </div>
  );
};
